import { ReactNode, useEffect, useRef, useState } from 'react';

import { NoticeModal } from 'components/ui/notice-modal';

interface DraftNoticeProps {
  /** Chave do `localStorage` que marca o aviso como já visto. Com ela gravada, o aviso não abre. */
  storageKey: string;
  title: string;
  /** Parágrafos do aviso. Viram a descrição acessível do diálogo. */
  children: ReactNode;
  actionLabel: string;
  /** Retoma o rascunho. */
  onResume: () => void;
  /** O destino abre sobre a Home, então retomar também fecha o aviso. */
  closeOnResume?: boolean;
  /** O aviso saiu do caminho: foi fechado, ou nem chegou a abrir. */
  onClose: () => void;
}

/**
 * Aviso automático de rascunho salvo (realização ou reconhecimento em preenchimento). Abre uma
 * vez: fechar ou retomar grava `storageKey`, e a partir daí ele só avisa `onClose`.
 */
export function DraftNotice({
  storageKey,
  title,
  children,
  actionLabel,
  onResume,
  closeOnResume = false,
  onClose,
}: DraftNoticeProps) {
  const [isOpen, setIsOpen] = useState(false);
  // A Home passa `onClose` inline (função nova a cada render); a ref evita reler a marca e avisar de novo.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (window.localStorage.getItem(storageKey)) {
      onCloseRef.current();
    } else {
      setIsOpen(true);
    }
  }, [storageKey]);

  function dismiss() {
    onClose();
    setIsOpen(false);
  }

  function handleClose() {
    window.localStorage.setItem(storageKey, 'true');
    dismiss();
  }

  function handleResume() {
    window.localStorage.setItem(storageKey, 'true');
    onResume();
    if (closeOnResume) dismiss();
  }

  return (
    <NoticeModal
      isOpen={isOpen}
      title={title}
      actionLabel={actionLabel}
      onAction={handleResume}
      onClose={handleClose}
      closeLabel="Fechar aviso"
    >
      {children}
    </NoticeModal>
  );
}
